import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { ManagedTableInfo, TableZoneInfo } from '@/types/api';
import { X } from 'lucide-react';

interface TableModalProps {
  restaurantId: number;
  floorId: number;
  table: ManagedTableInfo | null;
  zones: TableZoneInfo[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function TableModal({ restaurantId, floorId, table, zones, onClose, onSuccess }: TableModalProps) {
  const [formData, setFormData] = useState({
    name: table?.name || '',
    capacity: table?.capacity ?? 4,
    shape: table?.shape || 'SQUARE',
    x: table?.x ?? 0,
    y: table?.y ?? 0,
    width: table?.width ?? 100,
    height: table?.height ?? 100,
    sortOrder: table?.sortOrder ?? 0,
    active: table?.active ?? true,
    zoneId: table?.zoneId ?? 0,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: (data: Partial<ManagedTableInfo>) =>
      apiClient.createManagedTable(restaurantId, floorId, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create table';
      setErrors({ submit: message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<ManagedTableInfo>) =>
      apiClient.updateManagedTable(restaurantId, table!.id, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update table';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (formData.capacity <= 0) {
      newErrors.capacity = 'Capacity must be greater than 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const submitData: Partial<ManagedTableInfo> = {
      name: formData.name || undefined,
      capacity: formData.capacity,
      shape: formData.shape,
      x: formData.x,
      y: formData.y,
      width: formData.width,
      height: formData.height,
      sortOrder: formData.sortOrder,
      active: formData.active,
      zoneId: formData.zoneId || undefined,
    };

    if (table) {
      await updateMutation.mutateAsync(submitData);
    } else {
      await createMutation.mutateAsync(submitData);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-lg w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {table ? 'Edit Table' : 'Add New Table'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="table-name" className="block text-sm font-medium text-gray-700 mb-1">
                Table Name
              </label>
              <input
                id="table-name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                placeholder="e.g., Table 1"
              />
            </div>
            <div>
              <label htmlFor="table-capacity" className="block text-sm font-medium text-gray-700 mb-1">
                Capacity *
              </label>
              <input
                id="table-capacity"
                type="number"
                value={formData.capacity}
                onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                className={`input w-full ${errors.capacity ? 'border-red-500' : ''}`}
                min={1}
              />
              {errors.capacity && <p className="text-red-500 text-xs mt-1">{errors.capacity}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="table-shape" className="block text-sm font-medium text-gray-700 mb-1">
                Shape
              </label>
              <select
                id="table-shape"
                value={formData.shape}
                onChange={(e) => setFormData({ ...formData, shape: e.target.value })}
                className="input w-full"
              >
                <option value="SQUARE">Square</option>
                <option value="CIRCLE">Circle</option>
                <option value="RECTANGLE">Rectangle</option>
              </select>
            </div>
            <div>
              <label htmlFor="table-zone" className="block text-sm font-medium text-gray-700 mb-1">
                Zone
              </label>
              <select
                id="table-zone"
                value={formData.zoneId}
                onChange={(e) => setFormData({ ...formData, zoneId: parseInt(e.target.value) || 0 })}
                className="input w-full"
              >
                <option value={0}>-- No Zone --</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <label htmlFor="table-x" className="block text-sm font-medium text-gray-700 mb-1">
                X
              </label>
              <input
                id="table-x"
                type="number"
                value={formData.x}
                onChange={(e) => setFormData({ ...formData, x: parseInt(e.target.value) || 0 })}
                className="input w-full"
              />
            </div>
            <div>
              <label htmlFor="table-y" className="block text-sm font-medium text-gray-700 mb-1">
                Y
              </label>
              <input
                id="table-y"
                type="number"
                value={formData.y}
                onChange={(e) => setFormData({ ...formData, y: parseInt(e.target.value) || 0 })}
                className="input w-full"
              />
            </div>
            <div>
              <label htmlFor="table-width" className="block text-sm font-medium text-gray-700 mb-1">
                Width
              </label>
              <input
                id="table-width"
                type="number"
                value={formData.width}
                onChange={(e) => setFormData({ ...formData, width: parseInt(e.target.value) || 0 })}
                className="input w-full"
              />
            </div>
            <div>
              <label htmlFor="table-height" className="block text-sm font-medium text-gray-700 mb-1">
                Height
              </label>
              <input
                id="table-height"
                type="number"
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: parseInt(e.target.value) || 0 })}
                className="input w-full"
              />
            </div>
          </div>

          <div>
            <label htmlFor="table-sort-order" className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <input
              id="table-sort-order"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
              className="input w-full"
            />
          </div>

          <div className="flex items-center">
            <input
              id="table-active"
              type="checkbox"
              checked={formData.active}
              onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="table-active" className="ml-2 text-sm text-gray-700">
              Active
            </label>
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : table
                ? 'Update Table'
                : 'Create Table'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
